import PropTypes from 'prop-types';

import {
	CarouselBackgroundImage,
	CarouselBackgroundLazyImageContainer,
	MultiLayerCarouselButton,
	MultiLayerCarouselDescription,
	CarouselForegroundImage,
	CarouselForegroundImageContainer,
	CarouselOverlay,
	MultiLayerCarouselTitle
} from '@comeon/ui/src/carousel/base.tsx';
import { MultiLayerCarouselItem } from '@comeon/ui';
import LazyLoadImage from '~/components/utilities/lazyload-image.js';
import { getSmallestSrcFromSrcSet } from '~/components/utilities/image-format';

const sizes = {
	foreground: '(max-width: 375px) 300px, (max-width: 768px) 640px, (max-width: 1028px) 1080px, 1080w',
	background: '(max-width: 375px) 420px, (max-width: 768px) 860px, (max-width: 1028px) 1080px, 1920w'
};

const CarouselItem = ({
	id,
	index,
	backgroundImage,
	foregroundImage,
	backgroundImageURL,
	foregroundImageURL,
	externalWindow,
	buttonText,
	headerText,
	messageText,
	link,
	carouselItemClick,
	isSmall
}) => {
	const isPictureCompatible = !!(backgroundImage && foregroundImage);

	const body = (
		<>
			{isPictureCompatible ? (
				<CarouselBackgroundLazyImageContainer>
					<LazyLoadImage
						alt={link}
						src={getSmallestSrcFromSrcSet(backgroundImage.srcSet)}
						srcSet={backgroundImage.srcSet}
						sizes={sizes.background}
						pictureElementData={backgroundImage.sources}
						loading="eager"
					/>
				</CarouselBackgroundLazyImageContainer>
			) : (
				<CarouselBackgroundImage src={backgroundImageURL} alt={link} />
			)}
			<CarouselForegroundImageContainer>
				{isPictureCompatible ? (
					<LazyLoadImage
						alt={link}
						src={foregroundImage.srcSmall}
						srcSet={foregroundImage.srcSet}
						sizes={sizes.foreground}
						pictureElementData={foregroundImage.sources}
						loading="eager"
					/>
				) : (
					<CarouselForegroundImage src={foregroundImageURL} alt={link} />
				)}
			</CarouselForegroundImageContainer>
			<CarouselOverlay />
			{headerText && <MultiLayerCarouselTitle dataAt="carousel-title">{headerText}</MultiLayerCarouselTitle>}
			{messageText && (
				<MultiLayerCarouselDescription
					className="carousel-multilayer-description"
					dataAt="carousel-description"
				>
					{messageText}
				</MultiLayerCarouselDescription>
			)}
			{!isSmall && buttonText && (
				<MultiLayerCarouselButton dataAt="carousel-button">{buttonText}</MultiLayerCarouselButton>
			)}
		</>
	);

	return (
		<MultiLayerCarouselItem
			carouselItemClick={carouselItemClick}
			link={link}
			externalWindow={externalWindow}
			headerText={headerText}
			id={id}
			index={index}
		>
			{body}
		</MultiLayerCarouselItem>
	);
};

CarouselItem.propTypes = {
	backgroundImage: PropTypes.shape({
		src: PropTypes.string,
		srcSet: PropTypes.string,
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				srcSet: PropTypes.string,
				type: PropTypes.string
			})
		)
	}),
	backgroundImageURL: PropTypes.string,
	buttonText: PropTypes.string,
	carouselItemClick: PropTypes.func,
	foregroundImage: PropTypes.shape({
		src: PropTypes.string,
		srcSet: PropTypes.string,
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				srcSet: PropTypes.string,
				type: PropTypes.string
			})
		)
	}),
	foregroundImageURL: PropTypes.string,
	externalWindow: PropTypes.bool,
	headerText: PropTypes.string,
	messageText: PropTypes.string,
	index: PropTypes.number,
	isSmall: PropTypes.bool,
	link: PropTypes.string
};

CarouselItem.defaultProps = {
	backgroundImage: undefined,
	backgroundImageURL: undefined,
	buttonText: undefined,
	carouselItemClick: () => {},
	foregroundImage: undefined,
	foregroundImageURL: undefined,
	externalWindow: false,
	headerText: undefined,
	messageText: undefined,
	index: undefined,
	isSmall: false,
	link: undefined
};

export default CarouselItem;
